import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_STARTER, CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE } from 'self-service-api/constants/ApiNames';
import { PRODUCT_HUBSPOT_FREE } from 'self-service-api/constants/ProductIds';
import { currentOrderedPaidProductLevels, ENTERPRISE, FREE, orderedProductLevels, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { SOFTWARE } from 'self-service-api/constants/ProductTypes';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { CONTACT, MARKETING_CONTACT_DEPRECATED } from 'self-service-api/constants/UnitTypes';
import { CURRENT, DRAFT } from 'self-service-api/constants/VersionTypes';
import getProductBillingNameKey from 'self-service-api/core/utilities/getProductBillingNameKey';
import { isMarketingContactsSku } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { isCoreSeatSku } from 'self-service-api/core/utilities/seatUtils';
import { getIsUngatedForSeats } from './gatingUtils'; // TODO: remove once we have the information of includedUnits for a product version

export const CoreSeatsQuantityByProduct = {
  [MARKETING]: {
    [STARTER]: 0,
    [PROFESSIONAL]: 3,
    [ENTERPRISE]: 5
  },
  [SALES]: {
    [STARTER]: 0,
    [PROFESSIONAL]: 0,
    [ENTERPRISE]: 0
  },
  [SERVICE]: {
    [STARTER]: 0,
    [PROFESSIONAL]: 0,
    [ENTERPRISE]: 0
  },
  [CMS]: {
    [STARTER]: 0,
    [PROFESSIONAL]: 3,
    [ENTERPRISE]: 5
  },
  [OPERATIONS]: {
    [STARTER]: 0,
    [PROFESSIONAL]: 1,
    [ENTERPRISE]: 1
  }
};
export const CoreSeatsQuantityBySuiteLevel = {
  [STARTER]: 1,
  [PROFESSIONAL]: 7,
  [ENTERPRISE]: 11
};
export const getProduct = ({
  productData: {
    productMap,
    productLineLevelToProductId,
    apiNameToProductId
  }
}, {
  productId,
  apiName,
  productLine,
  productLevel
}) => {
  let productIdToUse;
  if (productId) productIdToUse = productId;else if (apiName) productIdToUse = apiNameToProductId[apiName];else if (productLine && productLevel) {
    if (productLevel === FREE) {
      productIdToUse = PRODUCT_HUBSPOT_FREE;
    } else {
      productIdToUse = productLineLevelToProductId && productLineLevelToProductId[productLine] && productLineLevelToProductId[productLine][productLevel];
    }
  }
  if (!productIdToUse) return null;
  return productMap[productIdToUse];
};
export const getSkuMap = ({
  productData: {
    skuMap
  }
}) => skuMap;
export const getSku = (state, {
  skuId
}) => {
  return getSkuMap(state)[skuId];
};
export const getProductForSkuId = ({
  productData: {
    skuMap,
    productMap
  }
}, {
  skuId
}) => {
  const {
    productId
  } = skuMap[skuId];
  return productMap[productId];
};
export const getProductVersion = (state, {
  skuId,
  apiName,
  versionNumber
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;

  if (skuId) {
    const sku = skuMap[skuId];
    return sku.skuType === QUANTITY_PACK ? skuMap[sku.productVersionSkuId] : sku;
  }

  const allProductVersions = Object.values(skuMap).filter(sku => sku.skuType === PRODUCT_VERSION && getProductForSkuId(state, {
    skuId: sku.skuId
  }).apiName === apiName);
  return allProductVersions.find(({
    versionNumber: searchVersionNumber
  }) => searchVersionNumber === versionNumber);
};

const getVersionTypesToUse = state => {
  const isUngatedForSeats = getIsUngatedForSeats(state);

  if (isUngatedForSeats) {
    return [DRAFT, CURRENT];
  }

  return [CURRENT];
};

export const getCurrentProductVersion = (state, productIdentifier) => {
  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const isUngatedForSeats = getIsUngatedForSeats(state);

  if (!isUngatedForSeats) {
    const {
      currentSkuId
    } = product;
    return getProductVersion(state, {
      skuId: currentSkuId
    });
  } else {
    const {
      productVersions
    } = product;
    const acceptableVersionTypes = getVersionTypesToUse(state);
    return productVersions.map(skuId => getProductVersion(state, {
      skuId
    })).sort((a, b) => b.versionNumber - a.versionNumber).find(productVersion => Boolean(productVersion && acceptableVersionTypes.includes(productVersion.versionType)));
  }
};
export const getSkuIdForFreeProduct = state => {
  const product = getCurrentProductVersion(state, {
    productId: PRODUCT_HUBSPOT_FREE
  });
  return product && product.skuId || 0;
};
export const getCurrentSkuIdForProduct = (state, productIdentifier) => {
  const {
    productLevel
  } = productIdentifier;

  if (productLevel === FREE) {
    return getSkuIdForFreeProduct(state);
  }

  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const currentProductVersion = getCurrentProductVersion(state, {
    productId: product.id
  });
  return currentProductVersion && currentProductVersion.skuId;
};
export const CRM_SUITE_PRODUCTS = {
  [STARTER]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [PROFESSIONAL]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [ENTERPRISE]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS]
};
export const TB_SUITE_LEVEL_TO_API_NAME = {
  [STARTER]: CRM_SUITE_STARTER_BUNDLE,
  [PROFESSIONAL]: CRM_SUITE_PROFESSIONAL_BUNDLE,
  [ENTERPRISE]: CRM_SUITE_ENTERPRISE_BUNDLE
};
export const CORE_SEAT_LEVEL_TO_API_NAME = {
  [STARTER]: CORE_SEATS_STARTER,
  [PROFESSIONAL]: CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: CORE_SEATS_ENTERPRISE
};
export const getCurrentSkuIdsForProductLine = (state, {
  productLine
}) => {
  const {
    productData: {
      productLineLevelToProductId
    }
  } = state;
  return currentOrderedPaidProductLevels.map(productLevel => {
    const productId = productLineLevelToProductId[productLine][productLevel];
    return getCurrentSkuIdForProduct(state, {
      productId
    });
  });
};
export const isCrmSuiteProductLevel = productLevel => {
  return productLevel in CRM_SUITE_PRODUCTS;
};
export const getProductLevelForSkuId = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return product.productLevel;
};
export const getProductSkuIdIsBundle = (state, {
  skuId
}) => {
  const productVersion = getProductVersion(state, {
    skuId
  });
  return productVersion.isBundle;
};
export const getBundledProductLinesForSkuId = (state, {
  skuId
}) => {
  const isBundle = getProductSkuIdIsBundle(state, {
    skuId
  });

  if (!isBundle) {
    return [];
  }

  const productVersion = getProductVersion(state, {
    skuId
  });
  return productVersion.bundledProducts.map(subProductVersion => {
    const {
      productId
    } = subProductVersion;
    const subProduct = getProduct(state, {
      productId
    });
    return subProduct.productLine;
  });
};
export const getTBSuiteProductLevelForSkuIds = (state, {
  skuIds
}) => {
  // Create an object keyed by productLevel -> productLine -> boolean
  const foundProducts = Object.keys(TB_SUITE_LEVEL_TO_API_NAME).reduce((acc, productLevel) => {
    const {
      skuId: tbSuiteSkuId
    } = getCurrentProductVersion(state, {
      apiName: TB_SUITE_LEVEL_TO_API_NAME[productLevel]
    });
    const productLines = getBundledProductLinesForSkuId(state, {
      skuId: tbSuiteSkuId
    });
    return Object.assign({}, acc, {
      [productLevel]: productLines.reduce((productLevelAcc, productLine) => Object.assign({}, productLevelAcc, {
        [productLine]: false
      }), {})
    });
  }, {});
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  skuIds.forEach(skuId => {
    const {
      productId,
      skuType
    } = skuMap[skuId];
    const {
      productLine,
      productLevel
    } = productMap[productId];

    if (skuType === PRODUCT_VERSION && productLine && productLevel && isCrmSuiteProductLevel(productLevel) && productLine in foundProducts[productLevel]) {
      foundProducts[productLevel][productLine] = true;
    }
  });

  for (const productLevel of Object.keys(foundProducts)) {
    const productLines = Object.keys(foundProducts[productLevel]);

    if (productLines.every(productLine => foundProducts[productLevel][productLine])) {
      return productLevel;
    }
  }

  return null;
};
export const getCrmSuiteProductLevelForSkuIds = (state, {
  skuIds
}) => {
  // Create an object keyed by productLevel -> productLine -> boolean
  const foundProducts = Object.keys(CRM_SUITE_PRODUCTS).reduce((acc, productLevel) => {
    const productLines = CRM_SUITE_PRODUCTS[productLevel];
    return Object.assign({}, acc, {
      [productLevel]: productLines.reduce((productLevelAcc, productLine) => Object.assign({}, productLevelAcc, {
        [productLine]: false
      }), {})
    });
  }, {});
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  skuIds.forEach(skuId => {
    const {
      productId,
      skuType
    } = skuMap[skuId];
    const {
      productLine,
      productLevel
    } = productMap[productId];

    if (skuType === PRODUCT_VERSION && productLine && productLevel && isCrmSuiteProductLevel(productLevel) && productLine in foundProducts[productLevel]) {
      foundProducts[productLevel][productLine] = true;
    }
  });

  for (const productLevel of Object.keys(foundProducts)) {
    const productLines = Object.keys(foundProducts[productLevel]);

    if (productLines.every(productLine => foundProducts[productLevel][productLine])) {
      return productLevel;
    }
  }

  return null;
};
export const getTBSuiteProductForProductLevel = (state, {
  suiteProductLevel
}) => {
  const apiName = TB_SUITE_LEVEL_TO_API_NAME[suiteProductLevel];
  return getProduct(state, {
    apiName
  });
};
export const getSkuIdIsInTBSuiteForProductLevel = (state, {
  skuId,
  suiteProductLevel
}) => {
  if (!suiteProductLevel) {
    return false;
  }

  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const suiteProduct = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel
  });
  const currentSuiteProductVersion = getCurrentProductVersion(state, {
    productId: suiteProduct.id
  });
  const suiteProductLines = getBundledProductLinesForSkuId(state, {
    skuId: currentSuiteProductVersion.skuId
  });
  return suiteProductLines.includes(productLine) && productLevel === suiteProductLevel;
};
export const getSkuIdIsInCrmSuite = (state, {
  skuId,
  suiteProductLevel
}) => {
  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const crmSuiteProductLines = suiteProductLevel ? CRM_SUITE_PRODUCTS[suiteProductLevel] : [];
  return crmSuiteProductLines.includes(productLine) && productLevel === suiteProductLevel;
};
export const getSkuIdsContainOnlyTBSuite = (state, {
  skuIds
}) => {
  const suiteProductLevel = getTBSuiteProductLevelForSkuIds(state, {
    skuIds
  });
  if (!suiteProductLevel) return false;
  return skuIds.every(skuId => getSkuIdIsInTBSuiteForProductLevel(state, {
    skuId,
    suiteProductLevel
  }));
};
export const getSkuIdsContainOnlyCRMSuite = (state, {
  skuIds
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIds(state, {
    skuIds
  });
  if (!suiteProductLevel) return false;
  return skuIds.every(skuId => getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  }));
};
export const getTBSuiteProductLevelForProductConfigurations = (state, {
  productConfigurations
}) => getTBSuiteProductLevelForSkuIds(state, {
  skuIds: productConfigurations.map(({
    skuId
  }) => skuId)
});
export const getCrmSuiteProductLevelForProductConfigurations = (state, {
  productConfigurations
}) => getCrmSuiteProductLevelForSkuIds(state, {
  skuIds: productConfigurations.map(({
    skuId
  }) => skuId)
});
export const getHubProductVersionSkuIds = (state, {
  skuIds
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return skuIds.filter(skuId => {
    const sku = skuMap[skuId];
    const product = getProductForSkuId(state, {
      skuId
    });
    return sku.skuType === PRODUCT_VERSION && product.productType === SOFTWARE && product.productLine && product.productLevel;
  });
};
export const getHubProductVersionSkuIdsOrDefault = (state, {
  skuIds
}) => {
  const filtered = getHubProductVersionSkuIds(state, {
    skuIds
  });
  return filtered.length ? filtered : skuIds;
};
export const getBillingNameKey = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const product = getProductForSkuId(state, {
    skuId
  });
  const sku = skuMap[skuId];

  if (sku.skuType === QUANTITY_PACK) {
    return getProductBillingNameKey({
      sku
    });
  } else if (sku.skuType === PRODUCT_VERSION) {
    if (!product) return null;
    const {
      apiName
    } = product;
    return getProductBillingNameKey({
      apiName,
      sku
    });
  }

  return null;
};
export const getQuantityPackForProductVersion = (state, {
  productVersionSkuId,
  unitType,
  parcelName
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;

  if (!productVersionSkuId) {
    return null;
  }

  const {
    quantityPackDetails
  } = skuMap[productVersionSkuId];
  return quantityPackDetails.find(quantityPack => {
    if (unitType && unitType !== quantityPack.quantityUnit.unitType) {
      // Need this special case until MARKETING_CONTACT is removed
      if (!(unitType === CONTACT && quantityPack.quantityUnit.unitType === MARKETING_CONTACT_DEPRECATED)) {
        return false;
      }
    }

    return !(parcelName && parcelName !== quantityPack.parcelName);
  });
};
export const getProductVersionHasQuantityPack = (state, {
  productVersionSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    quantityPackDetails
  } = skuMap[productVersionSkuId];
  return !!quantityPackDetails.length;
};
export const getIncludedCoreSeatsQuantityForProductVersion = (state, {
  productVersionSkuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId: productVersionSkuId
  });
  return productLine && currentOrderedPaidProductLevels.includes(productLevel) ? CoreSeatsQuantityByProduct[productLine][productLevel] : 0;
};
export const getIncludedCoreSeatsQuantityForSuiteProductLevel = (state, {
  suiteProductLevel
}) => {
  return CoreSeatsQuantityBySuiteLevel[suiteProductLevel];
};
export const getSkuIdIsHub = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  return !!productLine && !!productLevel && skuType === PRODUCT_VERSION;
};
export const getSkuIdIsQuantityPack = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    skuType
  } = skuMap[skuId];
  return skuType === QUANTITY_PACK;
};
export const highestProductLevel = (...productLevels) => orderedProductLevels[Math.max(0, ...productLevels.map(productLevel => orderedProductLevels.indexOf(productLevel)))];
export const getHighestProductLevelFromSkuIds = (state, {
  skuIds
}) => {
  const productLevels = skuIds.map(skuId => {
    const {
      productLevel
    } = getProductForSkuId(state, {
      skuId
    });
    return productLevel;
  }).filter(productLevel => !!productLevel);
  return highestProductLevel(...productLevels);
};
export const getHighestProductLevelSkuIdsByProductLine = (state, {
  skuIds
}) => {
  return skuIds.reduce((agr, skuId) => {
    const {
      productLine,
      productLevel
    } = getProductForSkuId(state, {
      skuId
    });
    const currentSkuIdForProductLine = agr[productLine];

    if (currentSkuIdForProductLine) {
      const {
        productLevel: productLevelForCurrentSkuId
      } = getProductForSkuId(state, {
        skuId: currentSkuIdForProductLine
      });
      const isHigherProductLevel = orderedProductLevels.indexOf(productLevel) > orderedProductLevels.indexOf(productLevelForCurrentSkuId);
      agr[productLine] = isHigherProductLevel ? skuId : currentSkuIdForProductLine;
    } else {
      agr[productLine] = skuId;
    }

    return agr;
  }, {});
};
export const getSkuIdIsAddonOrService = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  return (!productLine || !productLevel) && skuType === PRODUCT_VERSION;
};
export const getMaxProductVersionQuantity = (state, {
  skuId
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    versionNumber
  } = skuMap[skuId];
  const productVersion = getProductVersion(state, {
    apiName,
    versionNumber
  });
  const entry = productVersion.maxTouchlessPurchasableQuantity;
  return entry === null ? Infinity : entry || 1;
};
export const getLowestPaidProductLevel = (state, {
  productLine
}) => currentOrderedPaidProductLevels.find(productLevel => {
  const product = getProduct(state, {
    productLine,
    productLevel
  });
  return !!product;
});
export const getIsMarketingContactsSku = (state, {
  skuId
}) => {
  const {
    skuId: productVersionSkuId
  } = getProductVersion(state, {
    skuId
  });
  return isMarketingContactsSku({
    skuMap: getSkuMap(state),
    skuId: productVersionSkuId
  });
};
export const getLowestPaidProductLevelFromList = (state, {
  productLine,
  productLevels
}) => currentOrderedPaidProductLevels.filter(productLevel => productLevels.includes(productLevel)).find(productLevel => {
  const product = getProduct(state, {
    productLine,
    productLevel
  });
  return !!product;
});
export const getPaidProductsLevelsForProductLine = (state, {
  productLine
}) => currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
  productLine,
  productLevel
}));
export const getPaidProductsForProductLine = (state, {
  productLine
}) => {
  const filteredLevels = currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
    productLine,
    productLevel
  }));
  if (!filteredLevels.length) return [];
  const products = filteredLevels.map(productLevel => getProduct(state, {
    productLine,
    productLevel
  }));
  return products;
};
export const getIsSalesOrServiceProOrEntSku = (state, {
  skuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  return [SALES, SERVICE].includes(productLine) && [PROFESSIONAL, ENTERPRISE].includes(productLevel);
};
export const getQuantifiableSkusForProductLine = (state, {
  productLine
}) => {
  const paidProducts = getPaidProductsForProductLine(state, {
    productLine
  });
  const skus = paidProducts.map(product => {
    const currentVersion = getCurrentProductVersion(state, {
      apiName: product.apiName
    });
    return currentVersion;
  });
  const quantifiableSkus = skus.filter(sku => sku && sku.quantifiable);
  return quantifiableSkus;
};
export const getIsCrmSuiteSalesOrServiceSkuProEnt = (state, {
  skuId,
  suiteProductLevel
}) => {
  const isProductLevelProOrEnt = [PROFESSIONAL, ENTERPRISE].includes(suiteProductLevel);
  const {
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const isSkuHub = getSkuIdIsHub(state, {
    skuId
  });
  const isSkuInCrmSuite = getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  });
  const isProductLineSalesOrService = [SALES, SERVICE].includes(productLine);
  return isSkuInCrmSuite && isSkuHub && isProductLineSalesOrService && isProductLevelProOrEnt;
};
export const getProductDataIsEmpty = state => {
  const {
    productData
  } = state;
  return Object.values(productData).every(val => {
    if (!val || Object.keys(val).length === 0) return true;
    return false;
  });
};
export const getIsCoreSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return isCoreSeatSku(product.apiName);
};
export const getIsPaidCoreSeatSku = (state, {
  skuId
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  return getIsCoreSeatSku(state, {
    skuId
  }) && [CORE_SEATS_STARTER, CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE].includes(apiName);
};
export const getCoreSeatSkuForProductLevel = (state, {
  productLevel
}) => {
  const apiName = CORE_SEAT_LEVEL_TO_API_NAME[productLevel];
  const productVersion = getCurrentProductVersion(state, {
    apiName
  });
  return productVersion;
};